import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import { PlusCircle, Send, Vote } from "lucide-react";
import { GovernanceDataTable } from "@/components/governance/GovernanceDataTable";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { useGovernance } from "@/hooks/useGovernance";

interface MeetingRow {
  id: string;
  title: string;
  committeeName: string;
  date: string;
  attendees: string[];
  referencedTorIds: string[];
  status: "draft" | "published";
}

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString(undefined, { month: "short", day: "2-digit", year: "numeric" });

const getVoteCounts = (votes: { vote: "yes" | "no" | "abstain" }[]) =>
  votes.reduce(
    (acc, item) => {
      acc[item.vote] += 1;
      return acc;
    },
    { yes: 0, no: 0, abstain: 0 },
  );

export default function MeetingMinutes() {
  const {
    getActiveFramework,
    committees,
    terms,
    meetings,
    votingDecisions,
    members,
    getMemberById,
    addMeeting,
    publishMinutes,
    addVotingDecision,
    getVotingDecisionsByMeeting,
  } = useGovernance();
  const activeFramework = getActiveFramework();
  const [committeeFilter, setCommitteeFilter] = useState<string>("all");
  const [selectedMeetingId, setSelectedMeetingId] = useState<string | null>(null);
  const [meetingSheetOpen, setMeetingSheetOpen] = useState(false);
  const [createMeetingOpen, setCreateMeetingOpen] = useState(false);
  const [newMeetingDraft, setNewMeetingDraft] = useState({
    title: "",
    committeeId: "",
    date: new Date().toISOString().slice(0, 10),
    agendaItemsText: "",
    decisionsText: "",
    referencedTorId: "",
  });
  const [newVoteDraft, setNewVoteDraft] = useState({
    title: "",
    description: "",
    deadline: "",
  });

  const committeeMap = useMemo(() => new Map(committees.map((item) => [item.id, item])), [committees]);
  const torMap = useMemo(() => new Map(terms.map((item) => [item.id, item])), [terms]);
  const filteredCommittees = useMemo(
    () => committees.filter((item) => (activeFramework ? item.frameworkId === activeFramework.id : false)),
    [activeFramework, committees],
  );

  const rows = useMemo(() => {
    const frameworkMeetings = meetings
      .filter((item) => (activeFramework ? item.frameworkId === activeFramework.id : false))
      .filter((item) => (committeeFilter === "all" ? true : item.committeeId === committeeFilter))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    return frameworkMeetings.map(
      (meeting) =>
        ({
          id: meeting.id,
          title: meeting.title,
          committeeName: committeeMap.get(meeting.committeeId)?.name ?? "Unknown Committee",
          date: meeting.date,
          attendees: meeting.attendees,
          referencedTorIds: meeting.referencedTorIds,
          status: meeting.status,
        }) satisfies MeetingRow,
    );
  }, [meetings, activeFramework, committeeFilter, committeeMap]);

  const selectedMeeting = useMemo(
    () => meetings.find((item) => item.id === selectedMeetingId),
    [meetings, selectedMeetingId],
  );
  const selectedMeetingVotes = useMemo(
    () => (selectedMeeting ? getVotingDecisionsByMeeting(selectedMeeting.id) : []),
    [selectedMeeting, getVotingDecisionsByMeeting, votingDecisions],
  );

  const columns: ColumnDef<MeetingRow>[] = [
    {
      accessorKey: "title",
      header: "Meeting Title",
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.title}</div>
          <div className="text-xs text-muted-foreground">{row.original.committeeName}</div>
        </div>
      ),
    },
    {
      accessorKey: "date",
      header: "Date",
      cell: ({ row }) => formatDate(row.original.date),
    },
    {
      accessorKey: "attendees",
      header: "Attendees",
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex items-center">
          {row.original.attendees.slice(0, 4).map((memberId, index) => {
            const member = getMemberById(memberId);
            return (
              <Avatar key={memberId} className={`h-7 w-7 border-2 border-background ${index > 0 ? "-ml-2" : ""}`}>
                <AvatarFallback name={member?.name}>{member?.name?.slice(0, 2).toUpperCase() ?? "NA"}</AvatarFallback>
              </Avatar>
            );
          })}
          {row.original.attendees.length > 4 ? (
            <span className="ml-2 text-xs text-muted-foreground">+{row.original.attendees.length - 4}</span>
          ) : null}
        </div>
      ),
    },
    {
      accessorKey: "referencedTorIds",
      header: "Referenced ToRs",
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-1">
          {row.original.referencedTorIds.length === 0 ? (
            <Badge variant="outline">None</Badge>
          ) : (
            row.original.referencedTorIds.map((torId) => {
              const tor = torMap.get(torId);
              return (
                <Badge key={torId} variant="secondary">
                  {tor?.version ?? torId}
                </Badge>
              );
            })
          )}
        </div>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <Badge variant={row.original.status === "published" ? "default" : "outline"}>
          {row.original.status}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: () => <div className="text-right">Actions</div>,
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex items-center justify-end gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setSelectedMeetingId(row.original.id);
              setMeetingSheetOpen(true);
            }}
          >
            View Notes
          </Button>
        </div>
      ),
    },
  ];

  const createMeeting = () => {
    if (!activeFramework || !newMeetingDraft.title.trim() || !newMeetingDraft.committeeId) return;
    addMeeting({
      frameworkId: activeFramework.id,
      committeeId: newMeetingDraft.committeeId,
      title: newMeetingDraft.title.trim(),
      date: new Date(newMeetingDraft.date).toISOString(),
      attendees:
        committeeMap.get(newMeetingDraft.committeeId)?.memberIds.slice(0, 4) ??
        members.slice(0, 3).map((member) => member.id),
      agendaItems: newMeetingDraft.agendaItemsText
        .split("\n")
        .map((item) => item.trim())
        .filter(Boolean),
      decisions: newMeetingDraft.decisionsText
        .split("\n")
        .map((item) => item.trim())
        .filter(Boolean),
      actionItems: [],
      referencedTorIds: newMeetingDraft.referencedTorId ? [newMeetingDraft.referencedTorId] : [],
    });
    setCreateMeetingOpen(false);
    setNewMeetingDraft({
      title: "",
      committeeId: "",
      date: new Date().toISOString().slice(0, 10),
      agendaItemsText: "",
      decisionsText: "",
      referencedTorId: "",
    });
  };

  const submitVoteFromMeeting = () => {
    if (!selectedMeeting || !newVoteDraft.title.trim()) return;
    addVotingDecision({
      meetingId: selectedMeeting.id,
      committeeId: selectedMeeting.committeeId,
      frameworkId: selectedMeeting.frameworkId,
      title: newVoteDraft.title.trim(),
      description: newVoteDraft.description.trim(),
      proposedBy: "m-2",
      deadline: newVoteDraft.deadline ? new Date(newVoteDraft.deadline).toISOString() : undefined,
    });
    setNewVoteDraft({ title: "", description: "", deadline: "" });
  };

  if (!activeFramework) {
    return (
      <div className="space-y-6">
        <div className="space-y-1">
          <h1 className="text-3xl font-medium text-foreground">Meeting Minutes</h1>
          <p className="text-muted-foreground">Set up your governance framework to start tracking meeting notes.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-3xl font-medium text-foreground">Meeting Minutes</h1>
          <p className="text-muted-foreground">
            Capture committee meeting notes, linked ToRs, and derive voting decisions after each meeting.
          </p>
        </div>
        <Button onClick={() => setCreateMeetingOpen(true)}>
          <PlusCircle className="mr-2 h-4 w-4" />
          New Meeting
        </Button>
      </div>

      <Card className="shadow-card">
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle>Board & Committee Meetings</CardTitle>
          <div className="w-full sm:w-72">
            <Select value={committeeFilter} onValueChange={setCommitteeFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by committee" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Committees</SelectItem>
                {filteredCommittees.map((committee) => (
                  <SelectItem key={committee.id} value={committee.id}>
                    {committee.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <GovernanceDataTable columns={columns} data={rows} emptyMessage="No meeting minutes found for this scope." />
        </CardContent>
      </Card>

      <Sheet open={meetingSheetOpen} onOpenChange={setMeetingSheetOpen}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle>{selectedMeeting?.title ?? "Meeting Notes"}</SheetTitle>
            <SheetDescription>
              {selectedMeeting
                ? `${committeeMap.get(selectedMeeting.committeeId)?.name ?? "Committee"} • ${formatDate(selectedMeeting.date)}`
                : "Meeting details"}
            </SheetDescription>
          </SheetHeader>

          {selectedMeeting ? (
            <div className="mt-6 space-y-6">
              <div>
                <p className="text-sm font-medium">Attendees</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {selectedMeeting.attendees.map((attendeeId) => (
                    <Badge key={attendeeId} variant="outline">
                      {getMemberById(attendeeId)?.name ?? attendeeId}
                    </Badge>
                  ))}
                </div>
              </div>

              <Separator />

              <div className="space-y-3">
                <p className="text-sm font-medium">Agenda Items</p>
                <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                  {selectedMeeting.agendaItems.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>

              <div className="space-y-3">
                <p className="text-sm font-medium">Decisions Made</p>
                <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                  {selectedMeeting.decisions.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>

              <div className="space-y-3">
                <p className="text-sm font-medium">Action Items</p>
                {selectedMeeting.actionItems.length ? (
                  <div className="space-y-2">
                    {selectedMeeting.actionItems.map((item) => (
                      <div key={item.id} className="rounded-md border p-3 text-sm">
                        <p className="font-medium">{item.description}</p>
                        <p className="mt-1 text-muted-foreground">
                          Assignee: {getMemberById(item.assigneeId)?.name ?? item.assigneeId} • Due {formatDate(item.dueDate)}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No action items captured.</p>
                )}
              </div>

              <div className="space-y-3">
                <p className="text-sm font-medium">Referenced ToRs</p>
                <div className="flex flex-wrap gap-2">
                  {selectedMeeting.referencedTorIds.length === 0 ? (
                    <Badge variant="outline">No ToRs linked</Badge>
                  ) : (
                    selectedMeeting.referencedTorIds.map((torId) => {
                      const tor = torMap.get(torId);
                      return (
                        <Link key={torId} to={`/governance/tor/${tor?.committeeId ?? selectedMeeting.committeeId}`}>
                          <Badge variant="secondary" className="cursor-pointer">
                            {torId} • {tor?.version ?? "draft"}
                          </Badge>
                        </Link>
                      );
                    })
                  )}
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">Voting Decisions from this Meeting</p>
                  <Badge variant="outline">{selectedMeetingVotes.length} linked vote(s)</Badge>
                </div>
                <div className="space-y-2">
                  {selectedMeetingVotes.length ? (
                    selectedMeetingVotes.map((decision) => {
                      const counts = getVoteCounts(decision.votes);
                      return (
                        <div key={decision.id} className="rounded-md border p-3 text-sm">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="font-medium">{decision.title}</p>
                              <p className="text-muted-foreground">{decision.description || "No description provided."}</p>
                            </div>
                            <Badge variant={decision.status === "open" ? "outline" : "secondary"}>
                              {decision.status}
                            </Badge>
                          </div>
                          <p className="mt-2 text-xs text-muted-foreground">
                            Yes {counts.yes} • No {counts.no} • Abstain {counts.abstain}
                          </p>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-sm text-muted-foreground">No voting decisions have been initiated yet.</p>
                  )}
                </div>

                <div className="space-y-3 rounded-md border p-3">
                  <p className="text-sm font-medium">Initiate Vote</p>
                  <Input
                    placeholder="Decision title"
                    value={newVoteDraft.title}
                    onChange={(event) => setNewVoteDraft((prev) => ({ ...prev, title: event.target.value }))}
                  />
                  <Textarea
                    placeholder="Decision context and rationale"
                    value={newVoteDraft.description}
                    onChange={(event) => setNewVoteDraft((prev) => ({ ...prev, description: event.target.value }))}
                  />
                  <Input
                    type="date"
                    value={newVoteDraft.deadline}
                    onChange={(event) => setNewVoteDraft((prev) => ({ ...prev, deadline: event.target.value }))}
                  />
                  <Button size="sm" onClick={submitVoteFromMeeting} disabled={!newVoteDraft.title.trim()}>
                    <Vote className="mr-2 h-4 w-4" />
                    Add Voting Decision
                  </Button>
                </div>
              </div>
            </div>
          ) : null}

          {selectedMeeting ? (
            <SheetFooter className="mt-6">
              <Button
                onClick={() => publishMinutes(selectedMeeting.id)}
                disabled={selectedMeeting.status === "published"}
              >
                <Send className="mr-2 h-4 w-4" />
                {selectedMeeting.status === "published" ? "Minutes Sent" : "Send Minutes"}
              </Button>
            </SheetFooter>
          ) : null}
        </SheetContent>
      </Sheet>

      <Sheet open={createMeetingOpen} onOpenChange={setCreateMeetingOpen}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>New Meeting Notes</SheetTitle>
            <SheetDescription>Create a meeting notes entry under the active governance framework.</SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="meeting-title">Meeting Title</Label>
              <Input
                id="meeting-title"
                value={newMeetingDraft.title}
                onChange={(event) => setNewMeetingDraft((prev) => ({ ...prev, title: event.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Committee</Label>
              <Select
                value={newMeetingDraft.committeeId}
                onValueChange={(value) => setNewMeetingDraft((prev) => ({ ...prev, committeeId: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select committee" />
                </SelectTrigger>
                <SelectContent>
                  {filteredCommittees.map((committee) => (
                    <SelectItem key={committee.id} value={committee.id}>
                      {committee.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="meeting-date">Date</Label>
              <Input
                id="meeting-date"
                type="date"
                value={newMeetingDraft.date}
                onChange={(event) => setNewMeetingDraft((prev) => ({ ...prev, date: event.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tor-reference">Referenced ToR</Label>
              <Select
                value={newMeetingDraft.referencedTorId || "none"}
                onValueChange={(value) =>
                  setNewMeetingDraft((prev) => ({ ...prev, referencedTorId: value === "none" ? "" : value }))
                }
              >
                <SelectTrigger id="tor-reference">
                  <SelectValue placeholder="Optional ToR reference" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No ToR reference</SelectItem>
                  {terms
                    .filter((tor) => tor.frameworkId === activeFramework.id)
                    .map((tor) => (
                      <SelectItem key={tor.id} value={tor.id}>
                        {tor.id} • {committeeMap.get(tor.committeeId)?.name ?? "Committee"}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="agenda-items">Agenda Items (one per line)</Label>
              <Textarea
                id="agenda-items"
                value={newMeetingDraft.agendaItemsText}
                onChange={(event) =>
                  setNewMeetingDraft((prev) => ({ ...prev, agendaItemsText: event.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="decisions">Decisions (one per line)</Label>
              <Textarea
                id="decisions"
                value={newMeetingDraft.decisionsText}
                onChange={(event) => setNewMeetingDraft((prev) => ({ ...prev, decisionsText: event.target.value }))}
              />
            </div>
          </div>
          <SheetFooter className="mt-6">
            <Button onClick={createMeeting} disabled={!newMeetingDraft.title.trim() || !newMeetingDraft.committeeId}>
              Create Meeting Notes
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
